import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, ExternalLink, Loader2, SearchX } from "lucide-react";
import { useLanguage, useT } from "@/lib/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
// Relative, not "@/…": real modules under Jest, no alias there — the same reason
// MarketplaceLearn.jsx gives for its own two.
import { formatMoneyCode } from "../lib/formatMoney";
import ConsultRateLabel from "./ConsultRateLabel";
import { consultOf, isPerMinute, isUnbookablePerMinute } from "./consultPricing";
import { SiteLink } from "./marketplaceLinks";
import PractitionerAbout from "./PractitionerAbout";
import LoadFailure from "../components/ui/LoadFailure";
import usePractitionerProfile from "../reviews/hooks/usePractitionerProfile";
import RatingSummary from "../reviews/components/RatingSummary";
import ReviewList from "../reviews/components/ReviewList";
import { reviewErrorText } from "../reviews/errorText";
import { REVIEWS } from "../constants/testIds/reviews";

/**
 * The practitioner's page on the platform's own storefront — `/marketplace/:slug`.
 *
 * WHY IT EXISTS. Until now the card WAS the whole surface: three services, no descriptions,
 * no way to read what anyone thought of the practice. A visitor comparing two practitioners
 * had to leave the marketplace for each of their sites and come back. This page is what the
 * card links to now, and it states the three things the card cannot: every active service
 * with the description the practitioner already wrote, the rating breakdown, and the
 * reviews themselves.
 *
 * TWO INDEPENDENT READS, AND THAT IS THE DESIGN, NOT AN ACCIDENT. The header/services/
 * summary come from `GET /marketplace/practitioners/{slug}`; the reviews come from their own
 * paginated endpoint inside `ReviewList`. Either can fail on its own and each says so on its
 * own: a broken review page still leaves a bookable practice on screen, and a broken profile
 * read does not also assert "no reviews yet". One read for both would have made every
 * failure blank the whole page.
 *
 * THREE ANSWERS TO A URL, NOT TWO. `usePractitionerProfile` separates `notFound` (404
 * `PRACTITIONER_NOT_FOUND` — a slug that names no active practice) from `loadError`
 * (everything else). A retry button on a slug that does not exist is a dead end dressed as
 * a control; a "this practice does not exist" sentence on a 500 is a lie about someone's
 * business. The two get different panels.
 *
 * "BOOK" STILL CROSSES THE ORIGIN. The sale happens on the practice's own site and the
 * affiliate referral has to travel in the URL to get there (`marketplaceLinks.SiteLink`
 * owns that rule for both marketplace pages). Only the profile itself is a platform page.
 *
 * THE PRACTICE CARD IS FIVERR'S SELLER CARD. Fiverr keeps the actions that answer "how do
 * I work with this person" in one card that stays beside the page while you read. Here that
 * card carries "Book a session" (gold, the primary act), "Visit website" (outlined: the
 * practitioner's own site, where their full story, contact form and portal live), the host
 * the visitor will land on, and what the practice offers. Both links go through `SiteLink`,
 * so they reach the practice's REAL origin (custom domain or subdomain, as the server states
 * it in `site_url`) with the referral attached, and fall back to `/t/{slug}` on a payload
 * that carries no `site_url`. Every figure in the card is one the payload already states:
 * no "member since", no languages, no response time — the API has none of them, and a
 * profile must not invent facts about someone's business.
 *
 * A per-minute service with no active rate is NOT advertised here either — booking it would
 * 409 `PRICING_NOT_SET`, so the row would be a dead end. Exactly the hide the card applies.
 */
export default function MarketplacePractitioner() {
  const { slug } = useParams();
  const t = useT("marketplace");
  const tv = useT("common");
  const { lang } = useLanguage();
  const { detail, notFound, loading, loadError, reload } = usePractitionerProfile(slug);

  return (
    <div className="min-h-screen bg-sol-midnight text-white" data-testid={REVIEWS.profilePage}>
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="flex items-center justify-between mb-8">
          <Link to="/marketplace" className="text-sm text-white/40 hover:text-sol-gold inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {t("profile.backToMarketplace")}
          </Link>
          <LanguageSwitcher variant="dark" />
        </div>

        {notFound && (
          <div
            data-testid={REVIEWS.profileNotFound}
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center"
          >
            <SearchX className="w-10 h-10 mx-auto mb-3 text-white/30" />
            <h1 className="font-display text-2xl mb-2">{t("profile.notFoundTitle")}</h1>
            <p className="text-sm text-white/50">{t("profile.notFoundBody")}</p>
          </div>
        )}

        {loadError && !notFound && (
          <LoadFailure
            message={reviewErrorText(loadError, { t, tv, fallback: tv("errors.loadFailed") })}
            onRetry={reload}
            retryLabel={tv("actions.retry")}
            testId={REVIEWS.profileLoadError}
            retryTestId="marketplace-practitioner-retry"
          />
        )}

        {loading && !detail && !loadError && !notFound && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-sol-gold" />
          </div>
        )}

        {/* ONE GRID, ONE PRACTICE CARD. On a wide screen the card sits in its own column
            beside the whole page and stays in view while the visitor reads services and
            reviews (Fiverr's seller/gig layout). On a narrow one the grid is off and the
            card simply follows the header in DOM order. It is rendered exactly once: a
            second, CSS-hidden copy for mobile would duplicate every id and link in it. */}
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-x-10">
          {detail && !notFound && (
            <>
              <div className="lg:col-start-1 lg:row-start-1">
                <PractitionerAbout profile={detail} t={t} lang={lang} />
              </div>
              <aside className="mt-8 lg:mt-0 lg:col-start-2 lg:row-start-1 lg:row-span-4 lg:self-start lg:sticky lg:top-8">
                <PracticeCard detail={detail} t={t} />
              </aside>
              <div className="lg:col-start-1">
                <ServicesSection detail={detail} t={t} />
                <div className="mt-10">
                  <RatingSummary summary={detail.rating_summary} t={t} variant="dark" />
                </div>
              </div>
            </>
          )}

          {/* MOUNTED INDEPENDENTLY of the profile read — the whole point of the two loads.
              Not mounted for a slug that names no practice: its endpoint answers the same
              404, and two "not found" panels say one thing twice. */}
          {!notFound && (
            <section className="mt-10 lg:col-start-1 min-w-0">
              <h2 className="font-display text-xl mb-4">{t("profile.reviewsTitle")}</h2>
              <ReviewList slug={slug} t={t} tv={tv} lang={lang} variant="dark" />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/** The host a visitor will land on, for the "continues on …" line. "" for anything that
 * is not an absolute URL (the `/t/{slug}` fallback has no host worth naming). */
const hostOf = (url) => {
  try { return new URL(url).host; } catch { return ""; }
};

function PracticeCard({ detail, t }) {
  const bookable = (detail.services || []).some((s) => !isUnbookablePerMinute(s));
  const host = hostOf(detail.site_url);
  const stats = [
    { key: "services", label: t("profile.servicesTitle"),
      value: (detail.services || []).filter((s) => !isUnbookablePerMinute(s)).length },
    { key: "courses", label: t("tab.courses"), value: Number(detail.courses_count) || 0 },
    { key: "products", label: t("tab.products"), value: Number(detail.products_count) || 0 },
  ].filter((row) => row.value > 0);
  return (
    <div
      data-testid={REVIEWS.profileCard}
      className="rounded-2xl bg-white/[0.04] border border-white/10 p-6"
    >
      <p className="font-display text-lg truncate">{detail.name}</p>
      {detail.tagline && <p className="text-xs text-white/50 mt-1 line-clamp-2">{detail.tagline}</p>}
      <div className="flex flex-col gap-3 mt-5">
        {bookable && (
          <SiteLink
            siteUrl={detail.site_url}
            tenantSlug={detail.slug}
            path="/reserver"
            data-testid={REVIEWS.profileBook}
            className="inline-flex items-center justify-center px-4 h-11 rounded-full bg-sol-gold text-black text-sm font-medium hover:bg-sol-gold/90 transition"
          >
            {t("profile.bookSession")}
          </SiteLink>
        )}
        <SiteLink
          siteUrl={detail.site_url}
          tenantSlug={detail.slug}
          path="/"
          data-testid={REVIEWS.profileVisitSite}
          className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-full border border-sol-gold/60 text-sol-gold text-sm font-medium hover:bg-sol-gold/10 transition"
        >
          {t("profile.visitSite")} <ExternalLink className="w-4 h-4" aria-hidden="true" />
        </SiteLink>
        {host && (
          <p className="text-[11px] text-white/40 text-center break-all" data-testid={REVIEWS.profileSiteHost}>
            {t("profile.continueOn", { host })}
          </p>
        )}
      </div>
      {stats.length > 0 && (
        <dl className="mt-5 pt-5 border-t border-white/10 space-y-2 text-sm">
          {stats.map((row) => (
            <div key={row.key} className="flex items-center justify-between gap-3">
              <dt className="text-white/50">{row.label}</dt>
              <dd className="m-0 tabular-nums text-white/80">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function ServicesSection({ detail, t }) {
  const services = (detail.services || []).filter((s) => !isUnbookablePerMinute(s));
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl mb-4">{t("profile.servicesTitle")}</h2>
      {services.length === 0 ? (
        <p className="text-sm text-white/50" data-testid="practitioner-services-empty">
          {t("profile.servicesEmpty")}
        </p>
      ) : (
        <ul className="m-0 p-0 space-y-3">
          {services.map((s) => (
            <li
              key={s.id}
              data-testid={REVIEWS.profileService(s.id)}
              className="list-none rounded-2xl bg-white/[0.03] border border-white/10 p-5 flex flex-wrap items-start justify-between gap-4"
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg">{s.name}</h3>
                {s.description && (
                  <p className="text-sm text-white/60 mt-1 whitespace-pre-line break-words">{s.description}</p>
                )}
                {s.duration_min > 0 && (
                  <p className="text-xs text-white/40 mt-2 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {t("profile.duration", { minutes: s.duration_min })}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-3 shrink-0">
                {/* A per-minute consultation states its RATE and its ceiling, never a
                    catalogue price: `price_cents` is 0 for one by design, and reading that
                    0 as "free" advertised every paid consult as free (QA-02). */}
                {isPerMinute(s) ? (
                  <ConsultRateLabel
                    consult={consultOf(s)}
                    testId={`practitioner-service-rate-${s.id}`}
                    variant="dark"
                  />
                ) : (
                  <span className="text-sol-gold font-medium">
                    {s.price_cents > 0 ? formatMoneyCode(s.price_cents, s.currency) : t("card.free")}
                  </span>
                )}
                <SiteLink
                  siteUrl={detail.site_url}
                  tenantSlug={detail.slug}
                  path="/reserver"
                  data-testid={REVIEWS.profileServiceBook(s.id)}
                  className="inline-flex items-center justify-center px-4 h-9 rounded-full bg-sol-gold text-black text-sm font-medium hover:bg-sol-gold/90 transition"
                >
                  {t("card.book")}
                </SiteLink>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
